import { Link } from '@remix-run/react';
import styled from 'styled-components';
import { Image } from '~/components/wrappers/Image';
import { fontFamilies } from '~/theme/typography';

export const PageContainer = styled.div`
  width: 100%;
  height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  background-color: ${({ theme }) => theme.colors.colorMain};
`;

export const ContentWrapper = styled.div<{ $galleryHeightVh?: number }>`
  display: flex;
  flex-wrap: wrap;
  min-height: ${({ $galleryHeightVh = 1 }) => `${$galleryHeightVh * 100}vh`};
  background-color: ${({ theme }) => theme.colors.colorMain};

  @media ${({ theme }) => theme.breakpoints.sm} {
    flex-direction: column;
    flex-wrap: nowrap;
    min-height: auto;
    padding-bottom: calc(8rem + env(safe-area-inset-bottom, 0px));
  }
`;

export const ImageColumn = styled.div`
  height: auto;
  width: min(70vh, 50vw);
  min-width: 0;
  flex-shrink: 0;
  background-color: ${({ theme }) => theme.colors.colorMain};
  position: relative;

  @media ${({ theme }) => theme.breakpoints.sm} {
    width: 100%;
    min-width: unset;
    aspect-ratio: 3 / 4;
  }
`;

export const DetailsColumn = styled.div`
  flex: 1;
  min-width: 50%;
  position: sticky;
  /* Sticks below the rolling header's mobile strip (BPES-254). */
  top: var(--rolling-header-offset, 0);
  align-self: flex-start;
  height: calc(100vh - var(--rolling-header-offset, 0px));
  overflow-y: auto;
  background-color: ${({ theme }) => theme.colors.colorMain};
  backface-visibility: hidden;

  @media ${({ theme }) => theme.breakpoints.sm} {
    min-width: 100%;
    position: relative;
    /* Not sticky here, so the desktop \`top\` would only shift it down a header's height. */
    top: auto;
    align-self: stretch;
    height: auto;
    min-height: auto;
    overflow-y: visible;
  }
`;

export const DetailsContent = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  padding: 4.25rem 2.5rem 2.5rem 2.5rem;
  color: ${({ theme }) => theme.colors.white};

  h1 {
    margin: 1rem 0;
  }

  @media ${({ theme }) => theme.breakpoints.md} {
    padding: 4.25rem 1.5rem 2.5rem 1.5rem;
  }

  @media ${({ theme }) => theme.breakpoints.sm} {
    padding: 1.5rem 1rem 8px 1rem;
    overflow: visible;
  }
`;

export const DetailsWrapper = styled.div`
  display: flex;

  @media (min-width: 1441px) {
    gap: clamp(0px, calc((100vw - 1440px) * 0.2833), 136px);
  }

  @media ${({ theme }) => theme.breakpoints.sm} {
    flex-direction: column;
  }
`;

export const DesktopOnly = styled.div`
  display: block;

  @media ${({ theme }) => theme.breakpoints.sm} {
    display: none;
  }
`;

export const MobileOnly = styled.div`
  display: none;

  @media ${({ theme }) => theme.breakpoints.sm} {
    display: block;
  }
`;

export const BackButtonContainer = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.white};
  font-family: ${fontFamilies.PantonRustHeavy};
  font-size: 1rem;
  font-weight: 100;
  line-height: normal;
  letter-spacing: 0.08px;
  text-transform: uppercase;
  cursor: pointer;
  padding: 0;
  margin-bottom: 1.5rem;

  &:hover {
    opacity: 0.7;
  }

  @media ${({ theme }) => theme.breakpoints.sm} {
    margin-bottom: 1rem;
  }
`;

export const BackArrowIcon = styled(Image)`
  width: 0.5625rem;
  height: 1rem;
`;

export const ProductTitle = styled.h1`
  color: ${({ theme }) => theme.colors.white};
  font-family: ${fontFamilies.CircularStd};
  font-size: 1.5rem;
  line-height: normal;
  letter-spacing: 0.12px;
  text-transform: uppercase;
  font-weight: 450;
  margin: 0 0 1rem 0;

  @media ${({ theme }) => theme.breakpoints.sm} {
    font-size: 1.125rem;
    letter-spacing: 0.09px;
  }
`;

export const DescriptionContainer = styled.div`
  color: ${({ theme }) => theme.colors.white};
  font-family: ${fontFamilies.CircularStd};
  font-size: 0.875rem;
  line-height: normal;
  letter-spacing: 0.07px;
  text-transform: uppercase;
  margin-bottom: 1rem;

  ul {
    list-style: disc;
    padding-left: 1.25rem;
    margin: 0 0 1rem 0;
  }

  ul li,
  li {
    color: ${({ theme }) => theme.colors.white};
    font-family: ${fontFamilies.CircularStd};
    font-size: 0.875rem;
    line-height: normal;
    letter-spacing: 0.07px;
    text-transform: uppercase;
    margin-bottom: 0.25rem;
  }

  p {
    color: ${({ theme }) => theme.colors.white};
    font-family: ${fontFamilies.CircularStd};
    font-size: 0.875rem;
    line-height: normal;
    letter-spacing: 0.07px;
    text-transform: uppercase;
    margin: 0 0 0.25rem 0;

    &:last-child {
      margin-bottom: 0;
    }
  }

  p span,
  span {
    color: ${({ theme }) => theme.colors.white};
    font-family: ${fontFamilies.CircularStd};
    font-size: 0.875rem;
    line-height: normal;
    letter-spacing: 0.07px;
    text-transform: uppercase;
  }

  @media ${({ theme }) => theme.breakpoints.sm} {
    margin-top: 1rem;

    p {
      margin: 0 0 1rem 0;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
`;

export const PriceContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 1.5rem;

  @media ${({ theme }) => theme.breakpoints.sm} {
    margin-bottom: 2rem;
  }
`;

export const PriceText = styled.span`
  color: ${({ theme }) => theme.colors.white};
  text-align: right;
  font-family: ${fontFamilies.PantonRustHeavy};
  font-size: 1.5rem;
  font-weight: 100;
  line-height: normal;
  letter-spacing: 0.5px;
`;

export const OriginalPriceText = styled.span`
  color: ${({ theme }) => theme.colors.colorSecondary};
  font-family: ${fontFamilies.PantonRustHeavy};
  font-size: 1.5rem;
  font-weight: 100;
  line-height: normal;
  letter-spacing: 0.5px;
  position: relative;
  margin-left: 0.5rem;

  &:before {
    position: absolute;
    content: '';
    left: 0;
    top: 50%;
    right: 0;
    border-top: 0.09375rem solid;
    border-color: inherit;
    transform: translateY(-50%);
  }
`;

export const MatchWithContainer = styled.div`
  position: relative;
  z-index: 0;
  width: 100%;
  flex-basis: 100%;
  padding: 40px 0;
  background-color: ${({ theme }) => theme.colors.colorMain};

  @media ${({ theme }) => theme.breakpoints.sm} {
    padding: 32px 0;
  }
`;

export const MatchWithText = styled.span`
  display: block;
  margin-bottom: 16px;
  padding: 0 40px;
  color: ${({ theme }) => theme.colors.white};
  font-family: ${fontFamilies.PantonRustHeavy};
  font-size: 24px;
  font-style: normal;
  font-weight: 100;
  line-height: normal;
  letter-spacing: 0.5px;
  text-transform: uppercase;

  @media ${({ theme }) => theme.breakpoints.sm} {
    padding: 0 16px;
  }
`;

export const MatchWithGrid = styled.div`
  display: none;

  @media (min-width: 601px) {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 0;
    width: 100%;
  }
`;

export const MatchWithCardDesktop = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;
  text-decoration: none;

  & img {
    object-fit: cover;
  }
`;

export const MatchWithCardImageWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 360 / 480;
  overflow: hidden;
`;

export const MatchWithCarouselWrapper = styled.div`
  display: none;

  @media ${({ theme }) => theme.breakpoints.sm} {
    display: block;
    padding: 0 16px;
    margin-top: 1rem;
  }
`;

export const MatchWithCarouselViewport = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const MatchWithCarouselContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 12px;
  padding: 0 4px;
`;

export const MatchWithCarouselSlide = styled.div`
  flex: 0 0 50%;
  min-width: 50%;
  box-sizing: border-box;
`;

export const MatchWithCardMobile = styled(Link)`
  display: flex;
  flex-direction: column;
  width: 100%;
  text-decoration: none;
  height: 100%;
`;

export const MatchWithCardMobileImageWrapper = styled.div`
  flex: 1;
  min-height: 0;
  aspect-ratio: 188 / 195;
  overflow: hidden;

  & img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const MatchWithCardMobileText = styled.div`
  min-height: 55px;
  padding: 0.5rem 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  color: ${({ theme }) => theme.colors.white};
  font-family: ${fontFamilies.CircularStd};
  text-align: center;
`;

export const MatchWithCardMobileTitle = styled.span`
  font-size: 0.75rem;
  line-height: 1.4;
  letter-spacing: 0.009375rem;
  text-transform: uppercase;
`;

export const MatchWithCardMobilePriceRow = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  font-family: ${fontFamilies.PantonRustHeavy};
  font-size: 0.75rem;
  font-weight: 100;
  letter-spacing: 0.03125rem;
  color: ${({ theme }) => theme.colors.white};
`;

export const MatchWithCardMobilePrice = styled.span`
  font-family: ${fontFamilies.PantonRustHeavy};
  font-size: 0.75rem;
  font-weight: 100;
  letter-spacing: 0.03125rem;
`;
